/**
 * Redux thunks for the Subscription Plans feature.
 *
 * These call the customer subscription API directly. Every key the server
 * answers with is the key the components already read, so a response is put in
 * the store as it arrives — there is no mapping layer here, and adding a field
 * on the server makes it available without touching this file.
 *
 * The envelope is the app's usual one: HTTP 200 carrying `is_error`, `flag` and
 * `data`. A failure is `is_error === 1` with the reason in `original_text`, so
 * every thunk checks that before reading `data`.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../service/api.service';
import {
  AddPaymentMethodInput,
  CancelSubscriptionInput,
  CancelSubscriptionResult,
  CreateSetupIntentInput,
  CreateSetupIntentResult,
  PayNowInput,
  PayNowResult,
  SetPaymentMethodInput,
  SetPaymentMethodResult,
  Subscription,
  SubscriptionPaymentMethod,
  SubscriptionTransactionsInput,
  SubscriptionTransactionsResult,
} from '../../types/subscription.type';

/** The message to show when a call fails, in the server's words where it has any. */
const failureText = (response: any, fallback: string): string =>
  response?.original_text || response?.message || fallback;

/** Loads every subscription belonging to the logged in customer. */
export const getSubscriptions = createAsyncThunk<
  Subscription[],
  void,
  { rejectValue: string }
>('subscription/getSubscriptions', async (_, { rejectWithValue }) => {
  try {
    const response = await API({
      api: 'subscription/list',
      type: 'GET',
    });

    if (response?.is_error === 1) {
      return rejectWithValue(failureText(response, 'Unable to load subscriptions.'));
    }

    // An empty list is a valid answer, not a failure — a customer may hold none.
    return response?.data || [];
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Unable to load subscriptions.');
  }
});

/**
 * Loads one subscription in full, for the main page.
 *
 * This is the call that carries the payment methods and the transaction
 * history; the list deliberately does not, so opening the dashboard does not
 * pay for pages nobody visited.
 */
export const getSubscriptionDetails = createAsyncThunk<
  Subscription,
  number,
  { rejectValue: string }
>('subscription/getSubscriptionDetails', async (subscriptionId, { rejectWithValue }) => {
  try {
    const response = await API({
      api: `subscription/details?subscription_id=${subscriptionId}`,
      type: 'GET',
    });

    if (response?.is_error === 1 || !response?.data) {
      return rejectWithValue(failureText(response, 'Unable to load this subscription.'));
    }

    return response.data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Unable to load this subscription.');
  }
});

/**
 * Cancels a subscription.
 *
 * What comes back depends on the business's settings. Where they have to
 * approve, the subscription stays active and `cancellation_status` is Pending;
 * otherwise it is cancelled on the spot and comes back Approved. The calling
 * component uses this to decide which result modal to show.
 *
 * Safe to send twice: the server answers the same way and raises no second
 * request, which matters because a customer who misses the modal presses the
 * button again.
 */
export const cancelSubscription = createAsyncThunk<
  CancelSubscriptionResult,
  CancelSubscriptionInput,
  { rejectValue: string }
>('subscription/cancelSubscription', async (payload, { rejectWithValue }) => {
  try {
    const response = await API({
      api: 'subscription/cancel',
      body: payload,
      type: 'POST',
    });

    if (response?.is_error === 1 || !response?.data) {
      return rejectWithValue(failureText(response, 'Unable to cancel this subscription.'));
    }

    return response.data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Unable to cancel this subscription.');
  }
});

/**
 * One page of charge history for a subscription, newest first.
 *
 * Its own call rather than part of the plan: history grows by a row a month and
 * is paged, so the page that shows it asks for what it needs instead of every
 * plan read carrying the lot.
 */
export const getSubscriptionTransactions = createAsyncThunk<
  SubscriptionTransactionsResult,
  SubscriptionTransactionsInput,
  { rejectValue: string }
>('subscription/getTransactions', async (payload, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams({
      subscription_id: String(payload.subscription_id),
    });
    if (payload.limit) query.set('limit', String(payload.limit));
    if (payload.offset) query.set('offset', String(payload.offset));

    const response = await API({
      api: `subscription/transactions?${query.toString()}`,
      type: 'GET',
    });

    if (response?.is_error === 1 || !response?.data) {
      return rejectWithValue(failureText(response, 'Unable to load the transaction history.'));
    }

    return response.data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Unable to load the transaction history.');
  }
});

/**
 * The customer's saved cards and bank accounts.
 *
 * The dashboard's own endpoint, which is why there is no subscription one: a
 * card belongs to the customer, not to a plan. `contract_id` is left off, so
 * every row comes back with `default` and `backup` as 0 — which of them a plan
 * bills is read from the plan's `source_id` and `secondary_source_id`.
 */
export const getSubscriptionPaymentSources = createAsyncThunk<
  SubscriptionPaymentMethod[],
  number,
  { rejectValue: string }
>('subscription/getPaymentSources', async (patientId, { rejectWithValue }) => {
  try {
    const response = await API({
      api: `details/get_customer_payment_source?patient_id=${patientId}`,
      type: 'GET',
    });

    if (response?.is_error === 1) {
      return rejectWithValue(failureText(response, 'Unable to load your payment methods.'));
    }

    // This endpoint answers with payment_sources at the top level, not under data.
    return response?.payment_sources || [];
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Unable to load your payment methods.');
  }
});

/**
 * First half of adding a card: mints the SetupIntent for Stripe.js to confirm.
 *
 * Separate from `payment/create_setup_intent`, which resolves the customer
 * through a contract — a customer who holds a subscription and no loan cannot
 * use that one at all.
 */
export const createSubscriptionSetupIntent = createAsyncThunk<
  CreateSetupIntentResult,
  CreateSetupIntentInput,
  { rejectValue: string }
>('subscription/createSetupIntent', async (payload, { rejectWithValue }) => {
  try {
    const response = await API({
      api: 'subscription/create_setup_intent',
      body: payload,
      type: 'POST',
    });

    if (response?.is_error === 1 || !response?.data) {
      return rejectWithValue(failureText(response, 'Unable to start adding this card.'));
    }

    return response.data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Unable to start adding this card.');
  }
});

/**
 * Second half: stores the confirmed card and points the subscription at it.
 *
 * `is_backup` sends it to the fallback slot instead of the primary, so adding
 * cover does not displace a card that still works.
 */
export const addSubscriptionPaymentMethod = createAsyncThunk<
  { source_id: number },
  AddPaymentMethodInput,
  { rejectValue: string }
>('subscription/addPaymentMethod', async (payload, { rejectWithValue }) => {
  try {
    const response = await API({
      api: 'subscription/add_payment_method',
      body: payload,
      type: 'POST',
    });

    if (response?.is_error === 1 || !response?.data) {
      return rejectWithValue(failureText(response, 'Unable to save this card.'));
    }

    return response.data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Unable to save this card.');
  }
});

/**
 * Points a subscription at a card the customer already has. No charge — this is
 * the "make this my default" switch.
 */
export const setSubscriptionPaymentMethod = createAsyncThunk<
  SetPaymentMethodResult,
  SetPaymentMethodInput,
  { rejectValue: string }
>('subscription/setPaymentMethod', async (payload, { rejectWithValue }) => {
  try {
    const response = await API({
      api: 'subscription/set_payment_method',
      body: payload,
      type: 'POST',
    });

    if (response?.is_error === 1 || !response?.data) {
      return rejectWithValue(failureText(response, 'Unable to change the payment method.'));
    }

    return response.data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Unable to change the payment method.');
  }
});

/**
 * Pays the coming period early, from the "How would you like to pay?" page.
 *
 * It is the recurring charge brought forward rather than a payment of its own,
 * so the server moves the next payment date by one cycle when it settles —
 * which is what that page promises.
 *
 * Read `requires_confirmation` before showing anything: when it is true nothing
 * has been charged yet and the caller has to confirm `client_secret` with
 * Stripe.js, which is where a 3-D Secure challenge is answered. A bank account
 * settles server-side and comes back with `charge_id` instead.
 */
export const paySubscriptionNow = createAsyncThunk<
  PayNowResult,
  PayNowInput,
  { rejectValue: string }
>('subscription/payNow', async (payload, { rejectWithValue }) => {
  try {
    const response = await API({
      api: 'subscription/pay_now',
      body: payload,
      type: 'POST',
    });

    if (response?.is_error === 1 || !response?.data) {
      return rejectWithValue(failureText(response, 'Unable to take this payment.'));
    }

    return response.data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Unable to take this payment.');
  }
});
